import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const TRACKING_KEYS = [
  "google_analytics_id",
  "meta_pixel_id",
  "tiktok_pixel_id",
  "custom_head_scripts",
  "custom_body_scripts",
  "tracking_tags",
];

const CONSENT_KEY = "rgpd_consent";

const hasTrackingConsent = () => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_KEY) === "accepted";
};

const scheduleWhenIdle = (fn: () => void) => {
  if (typeof window === "undefined") return () => {};

  const win = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof win.requestIdleCallback === "function") {
    const id = win.requestIdleCallback(() => fn(), { timeout: 2500 });
    return () => win.cancelIdleCallback?.(id);
  }

  const timeoutId = setTimeout(fn, 700);
  return () => clearTimeout(timeoutId);
};

export const useTrackingScripts = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;

    let cancelled = false;
    let cancelIdle = () => {};

    const load = async () => {
      if (!hasTrackingConsent() || cancelled) return;

      const { data } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", TRACKING_KEYS);

      if (!data || cancelled) return;
      setLoaded(true);

      const config: Record<string, any> = {};
      data.forEach((row) => {
        config[row.key] = row.value;
      });

      // Google Analytics
      const gaId = (typeof config.google_analytics_id === "string" ? config.google_analytics_id : String(config.google_analytics_id ?? "")).trim();
      if (gaId) {
        const script = document.createElement("script");
        script.async = true;
        script.defer = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
        document.head.appendChild(script);

        const inline = document.createElement("script");
        inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
        document.head.appendChild(inline);
      }

      // Meta Pixel
      const pixelId = (typeof config.meta_pixel_id === "string" ? config.meta_pixel_id : String(config.meta_pixel_id ?? "")).trim();
      if (pixelId) {
        const inline = document.createElement("script");
        inline.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
        document.head.appendChild(inline);

        const noscript = document.createElement("noscript");
        const img = document.createElement("img");
        img.height = 1;
        img.width = 1;
        img.style.display = "none";
        img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.appendChild(noscript);
      }

      // TikTok Pixel
      const tiktokId = (typeof config.tiktok_pixel_id === "string" ? config.tiktok_pixel_id : String(config.tiktok_pixel_id ?? "")).trim();
      if (tiktokId) {
        const inline = document.createElement("script");
        inline.textContent = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${tiktokId}');ttq.page()}(window,document,'ttq');`;
        document.head.appendChild(inline);
      }

      // Dynamic tracking tags
      try {
        let tags = config.tracking_tags;
        if (typeof tags === "string") tags = JSON.parse(tags);

        if (Array.isArray(tags)) {
          tags.forEach((tag: { code: string; placement: string; enabled: boolean }) => {
            if (!tag.enabled || !tag.code?.trim()) return;

            const target = tag.placement === "body" ? document.body : document.head;
            const container = document.createElement("div");
            container.innerHTML = tag.code;

            Array.from(container.childNodes).forEach((node) => {
              if (node.nodeName === "SCRIPT") {
                const s = document.createElement("script");
                const orig = node as HTMLScriptElement;

                if (orig.src) {
                  s.src = orig.src;
                  s.async = true;
                  s.defer = true;
                }
                if (orig.textContent) s.textContent = orig.textContent;
                target.appendChild(s);
              } else {
                target.appendChild(node.cloneNode(true));
              }
            });
          });
        }
      } catch {
        // ignore malformed tracking_tags JSON
      }

      // Custom head scripts
      const headScripts = (typeof config.custom_head_scripts === "string" ? config.custom_head_scripts : String(config.custom_head_scripts ?? "")).trim();
      if (headScripts) {
        const container = document.createElement("div");
        container.innerHTML = headScripts;

        Array.from(container.childNodes).forEach((node) => {
          if (node.nodeName === "SCRIPT") {
            const s = document.createElement("script");
            const orig = node as HTMLScriptElement;

            if (orig.src) {
              s.src = orig.src;
              s.async = true;
              s.defer = true;
            }
            if (orig.textContent) s.textContent = orig.textContent;
            document.head.appendChild(s);
          } else {
            document.head.appendChild(node.cloneNode(true));
          }
        });
      }

      // Custom body scripts
      const bodyScripts = (typeof config.custom_body_scripts === "string" ? config.custom_body_scripts : String(config.custom_body_scripts ?? "")).trim();
      if (bodyScripts) {
        const container = document.createElement("div");
        container.innerHTML = bodyScripts;

        Array.from(container.childNodes).forEach((node) => {
          if (node.nodeName === "SCRIPT") {
            const s = document.createElement("script");
            const orig = node as HTMLScriptElement;

            if (orig.src) {
              s.src = orig.src;
              s.async = true;
              s.defer = true;
            }
            if (orig.textContent) s.textContent = orig.textContent;
            document.body.appendChild(s);
          } else {
            document.body.appendChild(node.cloneNode(true));
          }
        });
      }
    };

    const tryLoad = () => {
      if (!hasTrackingConsent() || loaded) return;
      cancelIdle();
      cancelIdle = scheduleWhenIdle(load);
    };

    tryLoad();

    const onConsentChange = () => {
      tryLoad();
    };

    window.addEventListener("storage", onConsentChange);

    return () => {
      cancelled = true;
      cancelIdle();
      window.removeEventListener("storage", onConsentChange);
    };
  }, [loaded]);
};
